/**
 * ⚠️ TEMPORAIRE - SERVICE STRIPE POUR DÉMO
 * 
 * Ce fichier contient la clé secrète Stripe pour la démo.
 * À SUPPRIMER avant mise en production !
 * 
 * ⚠️ SÉCURITÉ: Les clés Stripe sont passées en paramètre au lieu d'être stockées
 * dans le localStorage pour des raisons de sécurité. En production, utilisez
 * un backend qui gère les clés de manière sécurisée.
 */

// Clés Stripe par défaut (MODE TEST uniquement - utilisées si le restaurant n'a pas configuré ses clés)
import { DEFAULT_STRIPE_SECRET_KEY, DEFAULT_STRIPE_PUBLIC_KEY } from "../environment.jsx";

/**
 * Créer une session Stripe Checkout
 * @param {Object} data - Données de la commande
 * @param {string} data.sk_stripe - Clé secrète Stripe (optionnelle, utilise la clé par défaut si absente)
 * @param {boolean} data.useDefaultKey - Force l'utilisation de la clé par défaut (pour paiements d'abonnements)
 */
export const createCheckoutSession = async (data) => {
  try {
    // Si useDefaultKey est true, forcer la clé par défaut (paiement abonnement application)
    // Sinon, utiliser la clé fournie ou la clé par défaut
    const secretKey = data.useDefaultKey ? DEFAULT_STRIPE_SECRET_KEY : (data.sk_stripe || DEFAULT_STRIPE_SECRET_KEY);
    
    if (data.useDefaultKey) {
      console.log('💳 Utilisation de la clé Stripe par défaut de l\'application (paiement abonnement)');
    } else if (data.sk_stripe) {
      console.log('✅ Utilisation de la clé Stripe du restaurant (paiement commande client)');
    } else {
      console.warn('⚠️ Aucune clé Stripe fournie. Utilisation de la clé par défaut.');
    }
    
    // 💱 Gestion des devises et montants minimums Stripe
    let currency = (data.currency || 'xof').toLowerCase();
    let amount = data.amount;
    
    // 🔄 CONVERSION XAF → XOF (Stripe ne supporte pas XAF directement)
    // XAF (Franc CFA Central) et XOF (Franc CFA Ouest) ont la même valeur
    if (currency === 'xaf') {
      console.log('ℹ️ Conversion XAF → XOF (même valeur : 1 XAF = 1 XOF)');
      currency = 'xof';
      // Pas de conversion de montant nécessaire : 1000 XAF = 1000 XOF
    }
    
    // ⚠️ Vérifier les montants minimums selon la devise APRÈS conversion
    const minimumAmounts = {
      'xof': 100,   // 100 XOF minimum
      'eur': 50,    // 50 centimes (0,50€) minimum
      'usd': 50,    // 50 cents ($0.50) minimum
    };
    
    const minimum = minimumAmounts[currency];
    if (minimum && amount < minimum) {
      const originalAmount = amount;
      amount = minimum;
      
      const currencyDisplay = {
        'xof': 'XOF',
        'eur': 'centimes EUR',
        'usd': 'cents USD'
      };
      
      console.warn(
        `⚠️ Montant trop faible (${originalAmount} ${currencyDisplay[currency] || currency.toUpperCase()}). ` +
        `Forcé au minimum de ${minimum} ${currencyDisplay[currency] || currency.toUpperCase()}.`
      );
    }
    
    // 📊 Log de la conversion finale
    if (data.currency?.toLowerCase() !== currency || data.amount !== amount) {
      console.log('📊 Conversion effectuée:', {
        original: { amount: data.amount, currency: data.currency },
        final: { amount, currency: currency.toUpperCase() }
      });
    }
    
    const response = await fetch('https://api.stripe.com/v1/checkout/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secretKey}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        'payment_method_types[]': 'card',
        'line_items[0][price_data][currency]': currency,
        'line_items[0][price_data][product_data][name]': `Commande ${data.reference}`,
        'line_items[0][price_data][product_data][description]': data.notes || 'Commande restaurant',
        'line_items[0][price_data][unit_amount]': amount.toString(),
        'line_items[0][quantity]': '1',
        'mode': 'payment',
        'success_url': data.success_url,
        'cancel_url': data.cancel_url,
        'customer_email': data.customer_email || '',
        'metadata[reference]': data.reference,
        'metadata[notes]': data.notes || '',
        'metadata[original_amount]': data.amount.toString(),
        'metadata[original_currency]': data.currency || 'xof',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Erreur Stripe');
    }

    const session = await response.json();
    
    console.log('✅ Session Stripe créée:', {
      sessionId: session.id,
      url: session.url,
      amount: session.amount_total,
      currency: session.currency
    });

    return {
      sessionId: session.id,
      url: session.url,
      status: 'created'
    };

  } catch (error) {
    console.error('❌ Erreur création session Stripe:', error);
    throw error;
  }
};

/**
 * Vérifier le statut d'un paiement
 * @param {string} sessionId - ID de la session Stripe
 * @param {string} sk_stripe - Clé secrète Stripe (optionnelle)
 */
export const getPaymentStatus = async (sessionId, sk_stripe = null) => {
  try {
    // Utiliser la clé fournie ou la clé par défaut
    const secretKey = sk_stripe || DEFAULT_STRIPE_SECRET_KEY;
    
    const response = await fetch(`https://api.stripe.com/v1/checkout/sessions/${sessionId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${secretKey}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Session introuvable');
    }

    const session = await response.json();
    
    console.log('✅ Statut paiement récupéré:', {
      sessionId: session.id,
      payment_status: session.payment_status,
      amount: session.amount_total,
      currency: session.currency
    });

    return {
      status: session.payment_status, // 'paid', 'unpaid', 'no_payment_required'
      payment_status: session.payment_status,
      reference: session.metadata?.reference,
      amount: session.amount_total,
      currency: session.currency,
      customer_email: session.customer_details?.email,
      payment_intent: session.payment_intent,
    };

  } catch (error) {
    console.error('❌ Erreur vérification paiement:', error);
    throw error;
  }
};

/**
 * Obtenir la clé publique Stripe par défaut
 * ⚠️ En production, cette fonction ne devrait pas être nécessaire car la clé publique
 * sera fournie directement par le backend
 */
export const getStripePublicKey = () => {
  return DEFAULT_STRIPE_PUBLIC_KEY;
};

// Export pour compatibilité avec l'ancien code
export const STRIPE_PUBLIC_KEY = DEFAULT_STRIPE_PUBLIC_KEY;
